/**
 * Turns any thrown value into words a customer or waiter can read. Technical terms (status codes, error
 * codes, "revision") never appear here; the correlation id is returned separately as a support reference.
 */
import { API_ERROR_CODES, isApiError } from '@/api/errors';

export interface ErrorDescription {
  title: string;
  message: string;
  /** Correlation id for support, when the server or client supplied one. */
  reference?: string;
}

export function describeError(error: unknown): ErrorDescription {
  if (!isApiError(error)) {
    return { title: 'Something went wrong', message: 'Please try again.' };
  }
  const reference = error.correlationId;

  if (error.hasCode(API_ERROR_CODES.CART_REVISION_CONFLICT)) {
    return {
      title: 'Your cart was updated',
      message: 'Please review your cart before placing the order.',
      reference,
    };
  }
  if (error.isNetworkError) {
    return {
      title: "Can't reach the server",
      message: 'Check your connection and try again.',
      reference,
    };
  }
  if (error.status === 401) {
    return { title: 'Your session has ended', message: 'Please sign in again.', reference };
  }
  if (error.status === 403) {
    return { title: 'Not allowed', message: "You don't have access to this.", reference };
  }
  if (error.status === 404) {
    return {
      title: 'Not found',
      message: "We couldn't find what you were looking for.",
      reference,
    };
  }
  if (error.status === 429) {
    // The server limits repeated attempts (sign-in). Trying again at once only extends the wait.
    return {
      title: 'Too many attempts',
      message: 'Please wait a few minutes and try again.',
      reference,
    };
  }
  if (error.status >= 500 || error.status === 0) {
    return {
      title: 'Something went wrong on our side',
      message: 'Please try again in a moment.',
      reference,
    };
  }
  return { title: "That didn't go through", message: 'Please check and try again.', reference };
}
